import type { Locale } from "@chams-pay/contracts";
import { cn } from "@chams-pay/design-system/lib/utils";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarPopover } from "./calendar-popover.js";

function parseDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return new Date();
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function dateValue(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(
    date.getUTCDate(),
  ).padStart(2, "0")}`;
}

function monthStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function monthAvailable(year: number, month: number, minimum: Date | null, maximum: Date | null) {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  return !(minimum && last < minimum) && !(maximum && first > maximum);
}

function yearAvailable(year: number, minimum: Date | null, maximum: Date | null) {
  const first = new Date(Date.UTC(year, 0, 1));
  const last = new Date(Date.UTC(year, 11, 31));
  return !(minimum && last < minimum) && !(maximum && first > maximum);
}

type CalendarView = "days" | "months" | "years";

export function DatePicker({
  locale,
  value,
  label,
  min,
  max,
  showLabel = false,
  className,
  invalid = false,
  describedBy,
  onBlur,
  onChange,
}: {
  readonly locale: Locale;
  readonly value: string;
  readonly label: string;
  readonly min?: string;
  readonly max?: string;
  readonly showLabel?: boolean;
  readonly className?: string;
  readonly invalid?: boolean | undefined;
  readonly describedBy?: string | undefined;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
}) {
  const selected = parseDate(value);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CalendarView>("days");
  const [visibleMonth, setVisibleMonth] = useState(() => monthStart(selected));
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const language = locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn";
  const minimum = min ? parseDate(min) : null;
  const maximum = max ? parseDate(max) : null;
  const visibleYear = visibleMonth.getUTCFullYear();
  const visibleMonthIndex = visibleMonth.getUTCMonth();
  const monthLabel = new Intl.DateTimeFormat(language, {
    month: "long",
    timeZone: "UTC",
  }).format(visibleMonth);
  const yearLabel = new Intl.NumberFormat(language, { useGrouping: false }).format(visibleYear);
  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, month) =>
        new Intl.DateTimeFormat(language, { month: "short", timeZone: "UTC" }).format(
          new Date(Date.UTC(2024, month, 1)),
        ),
      ),
    [language],
  );
  const displayLabel = value
    ? new Intl.DateTimeFormat(language, {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(selected)
    : "—";
  const weekdayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        new Intl.DateTimeFormat(language, { weekday: "narrow", timeZone: "UTC" }).format(
          new Date(Date.UTC(2024, 0, index + 1)),
        ),
      ),
    [language],
  );
  const leadingDays = (visibleMonth.getUTCDay() + 6) % 7;
  const gridStart = new Date(
    Date.UTC(visibleMonth.getUTCFullYear(), visibleMonth.getUTCMonth(), 1 - leadingDays),
  );
  const days = Array.from(
    { length: 42 },
    (_, index) =>
      new Date(
        Date.UTC(
          gridStart.getUTCFullYear(),
          gridStart.getUTCMonth(),
          gridStart.getUTCDate() + index,
        ),
      ),
  );
  const yearPageStart = Math.floor(visibleYear / 12) * 12;
  const years = Array.from({ length: 12 }, (_, index) => yearPageStart + index);
  const previousAvailable =
    view === "days"
      ? monthAvailable(visibleYear, visibleMonthIndex - 1, minimum, maximum)
      : view === "months"
        ? yearAvailable(visibleYear - 1, minimum, maximum)
        : years.some((year) => yearAvailable(year - 12, minimum, maximum));
  const nextAvailable =
    view === "days"
      ? monthAvailable(visibleYear, visibleMonthIndex + 1, minimum, maximum)
      : view === "months"
        ? yearAvailable(visibleYear + 1, minimum, maximum)
        : years.some((year) => yearAvailable(year + 12, minimum, maximum));

  useEffect(() => setVisibleMonth(monthStart(parseDate(value))), [value]);

  const movePrevious = () => {
    if (view === "days") {
      setVisibleMonth(new Date(Date.UTC(visibleYear, visibleMonthIndex - 1, 1)));
      return;
    }
    setVisibleMonth(
      new Date(Date.UTC(visibleYear - (view === "years" ? 12 : 1), visibleMonthIndex, 1)),
    );
  };

  const moveNext = () => {
    if (view === "days") {
      setVisibleMonth(new Date(Date.UTC(visibleYear, visibleMonthIndex + 1, 1)));
      return;
    }
    setVisibleMonth(
      new Date(Date.UTC(visibleYear + (view === "years" ? 12 : 1), visibleMonthIndex, 1)),
    );
  };

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        !rootRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      )
        setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative flex flex-col gap-1.5", className)}>
      {showLabel && (
        <span className="font-heading text-[11px] font-semibold text-muted-foreground">
          {label}
        </span>
      )}
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onBlur={onBlur}
        onClick={() => {
          setView("days");
          setOpen((current) => !current);
        }}
        className="chams-focusable flex h-11 w-full items-center gap-2 rounded-md border border-input bg-card px-3 font-heading text-sm font-semibold text-foreground"
      >
        <CalendarDays aria-hidden="true" className="size-4 text-muted-2" />
        <span className="chams-figure min-w-0 flex-1 truncate text-start">{displayLabel}</span>
      </button>
      <CalendarPopover
        open={open}
        anchorRef={buttonRef}
        panelRef={panelRef}
        align={locale === "ar" ? "end" : "start"}
        className="w-[20rem] rounded-lg border border-border bg-popover p-3 shadow-e3 animate-fade-up"
      >
        <div role="dialog" aria-label={label}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={!previousAvailable}
              className="chams-focusable grid size-10 place-items-center rounded-md text-muted-foreground hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-25"
              onClick={movePrevious}
            >
              <ChevronLeft aria-hidden="true" className="size-4 rtl:-scale-x-100" />
            </button>
            <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
              <button
                type="button"
                aria-pressed={view === "months"}
                className="chams-focusable inline-flex min-h-9 items-center gap-1 rounded-md px-2 font-heading text-sm font-bold text-foreground hover:bg-sunken aria-pressed:bg-primary-soft aria-pressed:text-primary-strong"
                onClick={() => setView((current) => (current === "months" ? "days" : "months"))}
              >
                {monthLabel}
                <ChevronDown aria-hidden="true" className="size-3.5" />
              </button>
              <button
                type="button"
                aria-pressed={view === "years"}
                className="chams-figure chams-focusable inline-flex min-h-9 items-center gap-1 rounded-md px-2 font-heading text-sm font-bold text-foreground hover:bg-sunken aria-pressed:bg-primary-soft aria-pressed:text-primary-strong"
                onClick={() => setView((current) => (current === "years" ? "days" : "years"))}
              >
                {yearLabel}
                <ChevronDown aria-hidden="true" className="size-3.5" />
              </button>
            </div>
            <button
              type="button"
              disabled={!nextAvailable}
              className="chams-focusable grid size-10 place-items-center rounded-md text-muted-foreground hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-25"
              onClick={moveNext}
            >
              <ChevronRight aria-hidden="true" className="size-4 rtl:-scale-x-100" />
            </button>
          </div>
          {view === "days" && (
            <>
              <div className="mb-1 grid grid-cols-7 gap-1" aria-hidden="true">
                {weekdayLabels.map((weekday, index) => (
                  <span
                    key={`${weekday}-${index}`}
                    className="grid h-7 place-items-center font-heading text-[10px] font-bold text-muted-2"
                  >
                    {weekday}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                  const candidate = dateValue(day);
                  const outside = day.getUTCMonth() !== visibleMonth.getUTCMonth();
                  const active = candidate === value;
                  const disabled =
                    (minimum !== null && day < minimum) || (maximum !== null && day > maximum);
                  return (
                    <button
                      key={candidate}
                      type="button"
                      disabled={disabled}
                      aria-pressed={active}
                      data-active={active}
                      data-outside={outside}
                      className="chams-focusable grid size-9 place-items-center rounded-md font-heading text-xs font-semibold text-foreground hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-25 data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[outside=true]:text-muted-2"
                      onClick={() => {
                        onChange(candidate);
                        setOpen(false);
                      }}
                    >
                      {day.getUTCDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {view === "months" && (
            <div className="grid grid-cols-3 gap-1.5">
              {monthNames.map((name, month) => {
                const active = month === visibleMonthIndex;
                return (
                  <button
                    key={month}
                    type="button"
                    disabled={!monthAvailable(visibleYear, month, minimum, maximum)}
                    aria-pressed={active}
                    data-active={active}
                    className="chams-focusable min-h-11 rounded-md px-2 font-heading text-xs font-semibold text-muted-foreground hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-25 data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                    onClick={() => {
                      setVisibleMonth(new Date(Date.UTC(visibleYear, month, 1)));
                      setView("days");
                    }}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          )}
          {view === "years" && (
            <div className="grid grid-cols-3 gap-1.5">
              {years.map((year) => {
                const active = year === visibleYear;
                return (
                  <button
                    key={year}
                    type="button"
                    disabled={!yearAvailable(year, minimum, maximum)}
                    aria-pressed={active}
                    data-active={active}
                    className="chams-figure chams-focusable min-h-11 rounded-md px-2 font-heading text-xs font-semibold text-muted-foreground hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-25 data-[active=true]:bg-primary data-[active=true]:text-primary-foreground"
                    onClick={() => {
                      setVisibleMonth(new Date(Date.UTC(year, visibleMonthIndex, 1)));
                      setView("days");
                    }}
                  >
                    {new Intl.NumberFormat(language, { useGrouping: false }).format(year)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </CalendarPopover>
    </div>
  );
}
